import { useMutation, useQuery } from '@apollo/client';
import { DetailCards } from 'components/detail/view/DetailCards';
import { DetailHeader } from 'components/detail/view/DetailHeader';
import { ActionButton } from 'elements/minute/sidebar/sidebarComponents/atomic/ActionButton';
import {
    CreateGuideStory,
    CreateGuideStoryVariables
} from 'graphql/mutation/__generated__/CreateGuideStory';
import {
    UpdateGuideStory,
    UpdateGuideStoryVariables
} from 'graphql/mutation/__generated__/UpdateGuideStory';
import {
    UnlinkStoryFromEntityType,
    UnlinkStoryFromEntityTypeVariables
} from 'graphql/mutation/__generated__/unlinkStoryFromEntityType';
import {
    G_CREATE_STORY,
    G_UNLINK_STORY_FROM_ENTITY_TYPE,
    G_UPDATE_STORY
} from 'graphql/mutation/guideMutation';
import {
    GuideQueryView,
    GuideQueryViewVariables
} from 'graphql/query/__generated__/GuideQueryView';
import { GUIDE_QUERY_VIEW } from 'graphql/query/guideQuery';
import { mapCardInputToFullCard } from 'libs/utils/card/cardUtils';
import { uniqueId } from 'lodash';
import { S_PageStyle } from 'pages/page/PageStyle';
import { ParserConfig } from 'pages/special/ImportDetailsPage/ParserConfig';
import { ImportOperationsResult } from 'pages/special/ImportDetailsPage/prepareImportPayloadData';
import { useEffect, useState } from 'react';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

export const ImportDetailsPage = (props: Props): JSX.Element => {
    const [fields, setFields] = useState<string[]>(null);
    const [importData, setImportData] = useState<ImportOperationsResult>(null);
    const [currentPayloadIndex, setCurrentPayloadIndex] = useState<number>(0);
    const [reuploadImages, setReuploadImages] = useState<boolean>(true);
    const [assignToListId, setAssignToListId] = useState<string | null>(null);

    const [guideId, setGuideId] = useState<string>('');

    const guideQuery = useQuery<GuideQueryView, GuideQueryViewVariables>(GUIDE_QUERY_VIEW, {
        variables: {
            guideIdOrPath: guideId
        },
        nextFetchPolicy: 'network-only',
        skip: !guideId
    });

    useEffect(() => {
        guideQuery.refetch();
    }, [guideId]);

    const [createDetailMutation] = useMutation<CreateGuideStory, CreateGuideStoryVariables>(
        G_CREATE_STORY
    );
    const [updateDetailMutation] = useMutation<UpdateGuideStory, UpdateGuideStoryVariables>(
        G_UPDATE_STORY
    );

    const [unlinkStoryMutation] = useMutation<
        UnlinkStoryFromEntityType,
        UnlinkStoryFromEntityTypeVariables
    >(G_UNLINK_STORY_FROM_ENTITY_TYPE);
    // const [addToCategoryMutation] = useMutation<AddStoryToCategory, AddStoryToCategoryVariables>(
    //     G_ADD_STORY_TO_CATEGORY
    // );

    // const [linkStoryMutation] = useMutation<LinkStoryToEntityType, LinkStoryToEntityTypeVariables>(
    //     G_LINK_STORY_TO_ENTITY_TYPE
    // );

    const [responses, setResponses] = useState<string[]>([]);

    const allPayloads = importData ? [...importData.creates, ...importData.updates] : [];

    async function createDetails() {
        for (const payload of importData.creates) {
            const resp = await createDetailMutation({
                variables: {
                    ...payload,
                    assignToEntityTypeId: assignToListId,
                    reuploadImages
                },
                onError: (resp) => {
                    setResponses((prev) => [...prev, `ERROR: ${JSON.stringify(resp)}`]);
                }
            });

            if (resp.data) {
                setResponses((prev) => [
                    ...prev,
                    `OK: ${JSON.stringify(resp.data?.g_createStoryToGuide?.story)}`
                ]);
            }
        }
    }

    async function updateDetails() {
        for (const payload of importData.updates) {
            const resp = await updateDetailMutation({
                variables: {
                    ...payload,
                    story: {
                        ...payload.story
                    },
                    reuploadImages
                },
                onError: (resp) => {
                    setResponses((prev) => [...prev, `ERROR: ${JSON.stringify(resp)}`]);
                }
            });

            if (resp.data) {
                setResponses((prev) => [
                    ...prev,
                    `OK: ${JSON.stringify(resp.data?.g_updateStoryToGuide?.story)}`
                ]);
            }
        }
    }

    async function unlinkDetails() {
        for (const story of importData.unlinks) {
            const resp = await unlinkStoryMutation({
                variables: {
                    storyId: story.storyId,
                    guideId,
                    guideEntityTypeId: assignToListId
                },
                onError: (resp) => {
                    setResponses((prev) => [...prev, `ERROR: ${JSON.stringify(resp)}`]);
                }
            });

            if (resp.data) {
                setResponses((prev) => [...prev, `OK: ${JSON.stringify(resp.data)}`]);
            }
        }
    }

    return (
        <S_ImportPage className={props.className}>
            <ParserConfig
                setFields={setFields}
                setImportData={(data) => {
                    setImportData(data);
                }}
                setResponses={setResponses}
                setGuideId={setGuideId}
                setReuploadImages={setReuploadImages}
                assignListId={assignToListId}
                setAssignToListId={setAssignToListId}
            />

            {fields && (
                <>
                    <S_Fields>
                        <div>FIELDS</div>
                        {fields.map((field) => (
                            <div key={field}>{`"${field}"`}</div>
                        ))}
                    </S_Fields>
                </>
            )}
            {importData && allPayloads && (
                <S_PreviewCode>
                    <S_Row>PAYLOADS</S_Row>
                    <S_Row>Create operations: {importData.creates?.length}</S_Row>
                    <S_Row>Update operations: {importData.updates?.length}</S_Row>
                    <S_Row>Unlink operations: {importData.unlinks?.length}</S_Row>
                    <S_Row>{importData.unlinks.map((e) => e.header?.title).join(', ')}</S_Row>
                    <S_Row>Current row no: {currentPayloadIndex}</S_Row>
                    <S_Row>
                        <ActionButton
                            text="Prev"
                            onClick={() => {
                                setCurrentPayloadIndex((prev) => prev - 1);
                            }}
                            disabled={currentPayloadIndex === 0}
                        />
                        <ActionButton
                            text="Next"
                            onClick={() => {
                                setCurrentPayloadIndex((prev) => prev + 1);
                            }}
                            disabled={currentPayloadIndex === allPayloads.length - 1}
                        />
                    </S_Row>

                    {allPayloads[currentPayloadIndex] && (
                        <pre>{JSON.stringify(allPayloads[currentPayloadIndex], null, 2)}</pre>
                    )}
                </S_PreviewCode>
            )}

            {importData && allPayloads && allPayloads?.[currentPayloadIndex] && (
                <S_PreviewPanel key={Math.random()}>
                    <DetailHeader
                        header={{
                            title: allPayloads[currentPayloadIndex].story?.header?.title,
                            text: allPayloads[currentPayloadIndex].story?.header?.text,
                            imageStyleId:
                                allPayloads[currentPayloadIndex].story?.header?.imageStyleId,
                            imageUrl: allPayloads[currentPayloadIndex].story?.header?.imageUrl
                        }}
                        skipTitle={false}
                    />
                    <DetailCards
                        cards={allPayloads[currentPayloadIndex]?.cards?.map(mapCardInputToFullCard)}
                    />
                </S_PreviewPanel>
            )}

            {importData && (
                <ActionButton
                    text="IMPORT ALL"
                    onClick={async () => {
                        const answer = confirm('Are you sure to import all?');

                        if (answer) {
                            await createDetails();
                            await updateDetails();
                            await unlinkDetails();
                        }
                    }}
                />
            )}

            {responses &&
                responses?.length > 0 &&
                responses.map((response, i) => (
                    <S_Response key={uniqueId()}>
                        {i}: {response}
                    </S_Response>
                ))}
        </S_ImportPage>
    );
};

const S_ImportPage = styled(S_PageStyle)`
    padding: 30px;
    padding-right: 550px;
`;

const S_Row = styled.div`
    margin-top: 20px;
    display: flex;
    flex-direction: row;
    gap: 10px;
`;

const S_Fields = styled.div`
    display: flex;
    flex-direction: column;
`;

const S_PreviewCode = styled.div`
    padding: 30px 0;
`;

const S_PreviewPanel = styled.div`
    position: fixed;
    right: 0;
    top: 0;
    bottom: 0;
    width: 530px;
    height: 100vh;
    z-index: 1;
    height: 100%;
    overflow: scroll;
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.05);
    background: #f6f9ff;
`;

const S_Response = styled.div`
    margin-bottom: 20px;
`;
